import { BASE_URL, authHeader } from '../integrations/APIClient';

const AZURE_FUNCTIONS = 'https://chesshoodbackend.azurewebsites.net';
const ORIGINS = [...new Set([BASE_URL, AZURE_FUNCTIONS]
  .map(value => String(value || '').replace(/\/+$/, '')).filter(Boolean))];
const GUEST_DAILY_KEY = 'chesshood:guest-seven-daily:v1';
const getDay = () => new Date().toISOString().slice(0, 10);

export async function puzzleTrainingRequest(path, { body, params, signal } = {}) {
  const headers = (await authHeader()) || new Headers();
  if (body) headers.set('Content-Type', 'application/json');
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') query.set(key, String(value));
  });
  // URLSearchParams.size is not implemented on all mobile Safari/WebView
  // variants and test polyfills. A missing qaMode must never enable real Pro.
  const queryString = query.toString();
  const suffix = queryString ? '?' + queryString : '';
  let lastError;
  for (let i = 0; i < ORIGINS.length; i += 1) {
    let response;
    try {
      response = await fetch(ORIGINS[i] + '/api/puzzle-training/' + path + suffix, {
        method: body ? 'POST' : 'GET',
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal,
      });
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      if (!body && i + 1 < ORIGINS.length) { lastError = error; continue; }
      throw error;
    }
    const raw = await response.text();
    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    let data = null;
    try { data = JSON.parse(raw); } catch {}
    // A 404 can mean "no matching Elo puzzle", NOT a missing backend route.
    // Retry Azure only for a genuine SPA/HTML rewrite or an unsupported route.
    const missingRoute = response.status === 405 || contentType.includes('text/html');
    if (missingRoute && i + 1 < ORIGINS.length) {
      lastError = new Error('puzzle_training_route_unavailable');
      continue;
    }
    if (!response.ok || !data || typeof data !== 'object' || Array.isArray(data)) {
      const remoteError = typeof data?.error === 'string' ? data.error : '';
      const message = remoteError ||
        (response.status === 404 ? 'puzzle_not_found_near_rating'
          : response.status === 401 ? 'login_required'
            : response.status === 403 ? 'puzzle_access_denied'
              : response.status === 503 ? 'puzzle_training_temporarily_unavailable'
                : 'puzzle_training_unavailable');
      const error = new Error(message);
      error.status = response.status;
      throw error;
    }
    return data;
  }
  throw lastError || new Error('puzzle_training_unavailable');
}

export function trainingErrorMessage(error, playerRating) {
  const status = Number(error?.status || 0);
  const code = String(error?.message || '');
  const rating = Number(playerRating);
  const band = Number.isFinite(rating) && rating > 0
    ? ' (' + Math.max(0, Math.round(rating) - 100) + '–' + (Math.round(rating) + 100) + ' Elo)'
    : '';
  if (status === 404 || code === 'puzzle_not_found_near_rating') {
    return 'Hood could not find a suitable puzzle' + band + '. Please try again or choose another training category.';
  }
  if (code === 'login_required' || status === 401) {
    return 'Please sign in again so Hood can load your saved chess training.';
  }
  if (code === 'puzzle_access_denied' || status === 403) {
    return 'This training level is not available. Return to your recommended rating house.';
  }
  if (code === 'daily_free_limit_reached' || status === 402) {
    return 'You have used your 7 free puzzles today. Come back tomorrow or unlock unlimited training.';
  }
  return 'Hood could not load your puzzle right now. Please tap Retry. Your free puzzle allowance has not changed unless a position was issued.';
}

function safeGuestState() {
  let saved = null;
  try { saved = JSON.parse(localStorage.getItem(GUEST_DAILY_KEY) || 'null'); } catch {}
  if (!saved || saved.date !== getDay() || !Array.isArray(saved.issued)) {
    return { date: getDay(), issued: [], lastTrack: '', lastIndex: 0 };
  }
  return saved;
}

export function guestDailyQuota() {
  const state = safeGuestState();
  const usedToday = Math.min(7, state.issued.length);
  return { freePerDay: 7, usedToday, remainingToday: 7 - usedToday,
    date: state.date, issued: [...state.issued] };
}

export function guestTrackResumeIndex(trackId) {
  const state = safeGuestState();
  return state.lastTrack === trackId ? Math.max(0, Number(state.lastIndex) || 0) : 0;
}

// Local guest allowance is only a convenience. The authenticated quota is
// enforced atomically on the server and follows the player across devices.
export function reserveGuestPuzzle(puzzleId, trackId, index) {
  const state = safeGuestState();
  const id = String(puzzleId || '');
  if (!id) return { allowed: false, ...guestDailyQuota() };
  if (!state.issued.includes(id)) {
    if (state.issued.length >= 7) return { allowed: false, ...guestDailyQuota() };
    state.issued.push(id);
  }
  state.lastTrack = trackId;
  state.lastIndex = index;
  try { localStorage.setItem(GUEST_DAILY_KEY, JSON.stringify(state)); } catch {}
  return { allowed: true, ...guestDailyQuota() };
}

const tokenKey = uid => 'chesshood:free-puzzle-request:' + uid;
export function currentDailyPuzzleToken(uid) {
  const key = tokenKey(uid);
  try {
    const state = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (state?.day === getDay() && state?.token) return state.token;
  } catch {}
  const token = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'training-' + Date.now() + '-' + Math.random().toString(36).slice(2);
  try { sessionStorage.setItem(key, JSON.stringify({ day: getDay(), token })); } catch {}
  return token;
}
export function nextDailyPuzzleToken(uid) {
  try { sessionStorage.removeItem(tokenKey(uid)); } catch {}
  return currentDailyPuzzleToken(uid);
}

export function trainingCheckoutToken(uid) {
  const key = 'chesshood:monthly-puzzle-checkout:' + uid;
  try {
    const stored = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (stored?.token && Date.now() - stored.at < 3600000) return stored.token;
  } catch {}
  const token = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'checkout-' + Date.now() + '-' + Math.random().toString(36).slice(2);
  try { sessionStorage.setItem(key, JSON.stringify({ token, at: Date.now() })); } catch {}
  return token;
}
